import React from 'react';
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import {
  TableContainer,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Table,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { X, PenBoxIcon, ShieldBan, LogIn, Search, PlusIcon, BadgeCheck } from 'lucide-react';
import { format } from "date-fns";
import CustomButton from '../../componentsII/Buttons';
import AddUpdateUserModal from '../../components/Masters/UserMaster/AddUpdateUserModal';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/slices/notificationSlice';
import { setSelectedYear, loginSuccess } from '../../redux/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';

const UserMaster = () => {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isAddUpdateUsersModalOpen, setIsAddUpdateUsersModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Pagination and filter states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    status: 'ALL',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  const getAllUsers = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
    try {
      const response = await api.get('/admin/getAllUsers', {
        params: {
          page: pageNumber + 1,
          limit,
          search: filterParams.search,
          status: filterParams.status === 'ALL' ? '' : filterParams.status,
          sortBy: filterParams.sortBy,
          sortOrder: filterParams.sortOrder
        }
      });
      setUsers(response.data.data);
      setTotalUsers(response.data.pagination.total);
    } catch (error) {
      dispatch(showNotification({
        message: 'Failed to fetch users',
        type: 'error',
      }));
    }
  };

  useEffect(() => {
    getAllUsers();
  }, [page, rowsPerPage, filters]);

  // Debounced search function
  const debouncedSearch = debounce((value) => { setFilters(prev => ({ ...prev, search: value })); setPage(0); }, 500);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  //revoke or grant api access
  const handleApiAccess = async (user) => {
    try {
      const response = await api.post(`/admin/manageUserAccess/?user_id=${user.id}`, {
        api_granted: !user.api_granted
      });
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: user.api_granted ? 'User API access revoked successfully' : 'User API access granted successfully',
          type: 'success',
        }));
        getAllUsers();
      } else {
        dispatch(showNotification({
          message: user.api_granted ? 'Failed to revoke user API access' : 'Failed to grant user API access',
          type: 'error',
        }));
      }
    } catch (error) {
      console.error('Error revoking API access:', error);
    }
  };

  //login as user
  const loginAsUser = async (user) => {
    try {
      const oldToken = localStorage.getItem("token");
      const response = await api.post(`/admin/loginAsUser/?user_id=${user.id}`);
      if (response.data.status == 200) {
        const userDetails = response.data?.userDetails;
        const token = response.data?.accessToken;

        userDetails.adminContext = {
          ...userDetails.adminContext,
          oldToken: oldToken,
        };

        localStorage.setItem("user", JSON.stringify(userDetails));
        dispatch(setSelectedYear(userDetails.selectedYear));
        localStorage.setItem("token", token);
        dispatch(loginSuccess({ user: userDetails, token: token }));
        dispatch(
          showNotification({
            message: 'Login Successful! Redirecting to your dashboard...',
            type: "success",
          })
        );
        navigate('/account/company-details');
      } else {
        dispatch(showNotification({
          message: response.data.message,
          type: 'error',
        }));
      }
    } catch (error) {
      console.error('Error Logging In', error);
    }
  };

  return (
    <>
      <PageHeader title="User">
        <div className="flex justify-between items-center mt-5 gap-4">
          <div className="flex items-center gap-4 flex-1">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search users..."
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => debouncedSearch(e.target.value)}
              className="min-w-[300px]"
            />

            {/* Status Filter */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, status: e.target.value }))
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="UNVERIFIED">Unverified</MenuItem>
                <MenuItem value="VERIFIED">Verified</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
              >
                <MenuItem value="created_at">Created Date</MenuItem>
                <MenuItem value="first_name">First Name</MenuItem>
                <MenuItem value="last_name">Last Name</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="last_login_at">Last Login</MenuItem>
              </Select>
            </FormControl>

            {/* Sort Order */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Order</InputLabel>
              <Select
                value={filters.sortOrder}
                label="Order"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, sortOrder: e.target.value }))
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </div>

          <CustomButton
            className="bg-green-600 hover:bg-green-700 text-white"
            text={<><PlusIcon /> Create User</>}
            click={() => setIsAddUpdateUsersModalOpen(true)}
          />
        </div>

        <TableContainer component={Paper} className="mt-4">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login At</TableCell>
                <TableCell>Last Login Ip</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.first_name}</TableCell>
                  <TableCell>{user.last_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell title={user.status} className="flex items-center gap-2">
                    {user.status === "UNVERIFIED" ? (
                      <BadgeCheck className="text-yellow-500" size={27} />
                    ) : (
                      <BadgeCheck className="text-green-500" size={27} />
                    )}
                  </TableCell>
                  <TableCell>
                    {user.last_login_at ? format(new Date(user.last_login_at), "dd-MM-yyyy") : ""}
                  </TableCell>
                  <TableCell>{user.last_login_ip}</TableCell>
                  <TableCell>
                    <div className='flex items-center gap-2'>
                      <IconButton
                        title="edit"
                        sx={{
                          backgroundColor: '#6e6e6e',
                          '&:hover': {
                            backgroundColor: '#595858',
                          },
                        }}
                        onClick={() => {
                          setSelectedUser(user);
                          setIsAddUpdateUsersModalOpen(true);
                        }}
                      >
                        <PenBoxIcon color='white' />
                      </IconButton>
                      <IconButton
                        sx={{
                          backgroundColor: user.api_granted ? '#16A34A' : 'red',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: user.api_granted ? 'red' : '#16A34A',
                          },
                        }}
                        title={user.api_granted ? 'CLICK TO REVOKE API ACCESS' : 'CLICK TO GRANT API ACCESS'}
                        onClick={() => handleApiAccess(user)}
                      >
                        <ShieldBan color='white' />
                      </IconButton>
                      <IconButton
                        sx={{
                          backgroundColor: '#4287f5',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#1b6ef2',
                          },
                        }}
                        title="Login as this user"
                        onClick={() => loginAsUser(user)}
                      >
                        <LogIn color='white' />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={7}
                  count={totalUsers}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) => 
                    `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <Dialog
          open={isAddUpdateUsersModalOpen}
          onClose={() => { setIsAddUpdateUsersModalOpen(false); setSelectedUser(null) }}
          maxWidth="md"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              {selectedUser ? "Edit User" : "Add New User"}
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => { setIsAddUpdateUsersModalOpen(false); setSelectedUser(null) }}
            />
          </div>
          <DialogContent>
            <AddUpdateUserModal
              defaultValues={selectedUser ? {
                id: selectedUser.id,
                firstName: selectedUser.first_name,
                lastName: selectedUser.last_name,
                email: selectedUser.email,
                phoneNo: selectedUser.phone_no,
                countryCode: selectedUser.country_id,
                showPasswordFields: false
              } : null}
              onClose={() => {
                setIsAddUpdateUsersModalOpen(false);
                setSelectedUser(null);
                getAllUsers(); // Refresh the list after adding/updating
              }}
            />
          </DialogContent>
        </Dialog>
      </PageHeader>
    </>
  );
};

export default UserMaster;